import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowLeft,
  Download,
  ArrowUpRight,
  Leaf,
  Hash,
  MapPin,
  Loader2,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InquiryForm from "@/components/InquiryForm";
import { smoothScrollTo } from "@/lib/SmoothScroll";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const revealVariant = {
  hidden: { y: "110%" },
  show: (i = 0) => ({
    y: "0%",
    transition: {
      duration: 1.05,
      delay: 0.15 + i * 0.12,
      ease: [0.19, 1, 0.22, 1],
    },
  }),
};
const MaskLine = ({ children, i = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      variants={revealVariant}
      initial="hidden"
      animate="show"
      custom={i}
      className="block will-change-transform"
    >
      {children}
    </motion.span>
  </span>
);

export const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API}/products/${slug}`)
      .then((res) => {
        setProduct(res.data);
        // Safety net: also dispatch the shared preselect event so InquiryForm
        // picks it up even if the child mounts after prop propagation.
        setTimeout(
          () =>
            window.dispatchEvent(
              new CustomEvent("preselect-product", {
                detail: { slug: res.data.slug },
              })
            ),
          200
        );
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const downloadPdf = () => {
    if (!product) return;
    const url = `${API}/products/${product.slug}/spec-sheet.pdf`;
    window.open(url, "_blank", "noopener");
  };

  const goToInquiry = () => {
    smoothScrollTo("#inquiry", { offset: -80 });
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <Navbar />
        <div className="flex items-center gap-3 text-[#0A192F]" data-testid="product-loading">
          <Loader2 className="animate-spin" size={22} />
          <span className="font-accent text-sm">Loading product…</span>
        </div>
      </main>
    );
  }

  if (notFound || !product) {
    return (
      <main className="min-h-screen bg-[#FAF9F6] flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-testid="product-not-found">
          <div className="font-display text-6xl text-[#0A192F] font-bold">404</div>
          <p className="mt-4 text-[#475569]">This product doesn&apos;t exist.</p>
          <button
            onClick={() => navigate("/#products")}
            className="mt-8 px-6 py-3 bg-[#0A192F] text-white text-xs tracking-[0.18em] uppercase"
          >
            Back to Products
          </button>
        </div>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-[#FAF9F6] text-[#0A192F] font-body"
      data-testid={`product-detail-${product.slug}`}
    >
      <Navbar />

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-[92svh] flex items-end text-white overflow-hidden"
      >
        <motion.div
          className="absolute inset-0 bg-cover bg-center will-change-transform"
          style={{
            backgroundImage: `url(${product.hero_image})`,
            y: imgY,
            scale: imgScale,
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-[#060F1E]/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060F1E]/40 via-transparent to-[#060F1E]" />
        <div className="absolute inset-0 grain-overlay pointer-events-none" />

        <div className="relative z-10 max-w-[1440px] mx-auto w-full px-6 md:px-12 pt-40 pb-16 md:pb-24">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.9 }}
            className="flex items-center gap-3 mb-8 font-accent text-[10px] text-white/60"
          >
            <Link
              to="/"
              className="hover:text-[#D4AF37] transition-colors"
              data-testid="breadcrumb-home"
            >
              Home
            </Link>
            <span>/</span>
            <Link
              to="/#products"
              className="hover:text-[#D4AF37] transition-colors"
              data-testid="breadcrumb-products"
            >
              Products
            </Link>
            <span>/</span>
            <span className="text-[#D4AF37]">{product.name}</span>
          </motion.div>

          <div className="flex items-center gap-4 mb-8">
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="h-px w-16 bg-[#D4AF37] origin-left"
            />
            <span className="font-accent text-[10px] text-[#D4AF37]">
              {product.tagline}
            </span>
          </div>

          <h1
            className="font-display font-bold leading-[0.9] tracking-tight max-w-5xl"
            data-testid="product-hero-title"
            style={{ fontSize: "clamp(3rem, 8vw, 8.5rem)" }}
          >
            <MaskLine i={0}>{product.name}</MaskLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={downloadPdf}
              className="group relative inline-flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-[#0A192F] font-semibold tracking-[0.16em] uppercase text-[11px] overflow-hidden"
              data-testid="product-download-spec-sheet"
            >
              <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              <span className="relative z-10">Download Spec Sheet</span>
              <Download size={14} className="relative z-10" />
            </button>
            <button
              onClick={goToInquiry}
              className="inline-flex items-center gap-3 px-8 py-4 border border-white/30 text-white font-semibold tracking-[0.16em] uppercase text-[11px] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
              data-testid="product-request-quote"
            >
              Request a Quote
              <ArrowUpRight size={14} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-24 md:py-32 bg-[#FAF9F6]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
              className="lg:col-span-7"
            >
              <div className="flex items-center gap-4 mb-6">
                <span className="h-px w-16 bg-[#D4AF37]" />
                <span className="font-accent text-[10px] text-[#475569]">
                  Overview
                </span>
              </div>
              <h2
                className="font-display font-bold text-[#0A192F] leading-tight tracking-tight"
                style={{ fontSize: "clamp(2rem, 4.2vw, 3.75rem)" }}
              >
                Built for buyers who cannot afford
                <span className="italic font-normal text-[#D4AF37]"> a bad shipment.</span>
              </h2>
              <p className="mt-8 text-[#475569] text-base md:text-lg leading-relaxed max-w-xl">
                {product.description}
              </p>
            </motion.div>

            <div className="lg:col-span-5 space-y-4">
              <FactRow
                icon={Leaf}
                label="Botanical"
                value={product.botanical || "—"}
                delay={0.15}
              />
              <FactRow
                icon={Hash}
                label="HS Code"
                value={product.hs_code || "—"}
                delay={0.25}
              />
              <FactRow
                icon={MapPin}
                label="Origin"
                value={product.origin || "India"}
                delay={0.35}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="py-24 md:py-32 bg-white border-t border-[#E2E8F0]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-4 mb-6">
                <span className="h-px w-16 bg-[#D4AF37]" />
                <span className="font-accent text-[10px] text-[#475569]">
                  Technical
                </span>
              </div>
              <motion.h3
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
                className="font-display font-bold text-[#0A192F] leading-tight tracking-tight"
                style={{ fontSize: "clamp(2rem, 3.6vw, 3.25rem)" }}
              >
                Specifications
              </motion.h3>
              <p className="mt-6 text-[#475569] text-sm md:text-base leading-relaxed max-w-md">
                All batches are tested against these baseline parameters.
                Buyer-specific specs are accepted for custom production runs.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="border-t border-[#E2E8F0]">
                {product.specs?.map((s, i) => (
                  <motion.div
                    key={s.k}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: i * 0.05 }}
                    className="flex items-center justify-between py-4 border-b border-[#E2E8F0]"
                    data-testid={`spec-row-${i}`}
                  >
                    <span className="font-accent text-[10px] text-[#475569]">
                      {s.k}
                    </span>
                    <span className="font-display text-lg text-[#0A192F] font-medium">
                      {s.v}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packaging / Apps / Shipping */}
      <section className="py-24 md:py-32 bg-[#0A192F] text-white grain-overlay overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            <DetailBlock
              index="01"
              title="Packaging"
              items={product.packaging || []}
              testid="detail-packaging"
            />
            <DetailBlock
              index="02"
              title="Applications"
              items={product.applications || []}
              testid="detail-applications"
            />
            <DetailBlock
              index="03"
              title="Shipping"
              items={product.shipping || []}
              testid="detail-shipping"
            />
          </div>

          <div className="mt-16 flex flex-wrap items-center gap-4">
            <button
              onClick={downloadPdf}
              className="group relative inline-flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-[#0A192F] font-semibold tracking-[0.16em] uppercase text-[11px] overflow-hidden"
              data-testid="product-download-spec-sheet-secondary"
            >
              <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
              <span className="relative z-10">Download Full PDF</span>
              <Download size={14} className="relative z-10" />
            </button>
            <Link
              to="/#products"
              className="inline-flex items-center gap-3 px-8 py-4 border border-white/30 text-white font-semibold tracking-[0.16em] uppercase text-[11px] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
              data-testid="back-to-products"
            >
              <ArrowLeft size={14} />
              All Products
            </Link>
          </div>
        </div>
      </section>

      {/* Inquiry */}
      <InquiryForm presetProduct={product.slug} />

      <Footer />
    </main>
  );
};

const FactRow = ({ icon: Icon, label, value, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.9, delay, ease: [0.19, 1, 0.22, 1] }}
    className="flex items-start gap-5 p-6 border border-[#E2E8F0] bg-white"
  >
    <div className="w-12 h-12 border border-[#D4AF37]/50 bg-[#FAF9F6] flex items-center justify-center flex-shrink-0">
      <Icon size={20} strokeWidth={1.4} className="text-[#D4AF37]" />
    </div>
    <div>
      <div className="font-accent text-[10px] text-[#475569]">{label}</div>
      <div className="font-display text-lg text-[#0A192F] mt-1 font-medium">
        {value}
      </div>
    </div>
  </motion.div>
);

const DetailBlock = ({ index, title, items, testid }) => (
  <div className="bg-[#0A192F] p-10 md:p-12" data-testid={testid}>
    <div className="flex items-center justify-between mb-8">
      <span
        className="font-display font-bold text-white/15"
        style={{ fontSize: "clamp(2.5rem, 4vw, 3.75rem)", lineHeight: 1 }}
      >
        {index}
      </span>
    </div>
    <h4 className="font-display text-2xl md:text-3xl font-bold">{title}</h4>
    <ul className="mt-6 space-y-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3 text-white/70 text-sm leading-relaxed">
          <span className="text-[#D4AF37] mt-1">◆</span>
          {item}
        </li>
      ))}
    </ul>
  </div>
);

export default ProductDetail;
